import type { ClassificationChange } from "./hooks/useCaseFilesystem";
import type { FsNode, TaxonomyCategory } from "./types";

/** One file's picks in the classify dialog. */
export type ClassificationPick = {
  category: string | null;
  documentType: string | null;
};

export const currentPick = (node: FsNode): ClassificationPick => ({
  category: node.taxonomy_category ?? null,
  documentType: node.taxonomy_category ? node.document_type ?? null : null
});

/** The document types a category offers; none for an unknown category. */
export const typesFor = (
  categories: TaxonomyCategory[],
  category: string | null
): string[] =>
  categories.find((row) => row.category === category)?.document_types ?? [];

/** A new category keeps the type only when the category offers it. */
export const withCategory = (
  categories: TaxonomyCategory[],
  pick: ClassificationPick,
  category: string | null
): ClassificationPick => ({
  category,
  documentType:
    category && typesFor(categories, category).includes(pick.documentType ?? "")
      ? pick.documentType
      : null
});

/**
 * The files whose picks changed, one request per (category, type) pair.
 * Unchanged files are left out so an automatic category isn't pinned (or a
 * blank one locked) just because the dialog was opened.
 */
export const classificationChanges = (
  nodes: FsNode[],
  picks: Record<string, ClassificationPick>
): ClassificationChange[] => {
  const groups = new Map<string, ClassificationChange>();
  const changed = (node: FsNode, pick: ClassificationPick) => {
    const before = currentPick(node);
    return (
      before.category !== pick.category ||
      before.documentType !== pick.documentType
    );
  };
  nodes.forEach((node) => {
    const pick = picks[node.id];
    if (!pick || !changed(node, pick)) return;
    const key = JSON.stringify([pick.category, pick.documentType]);
    const group = groups.get(key) ?? {
      nodeIds: [],
      category: pick.category,
      documentType: pick.documentType
    };
    group.nodeIds.push(node.id);
    groups.set(key, group);
  });
  return [...groups.values()];
};
